import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { FollowUpEntity, followUpSchema } from '@/entities/FollowUp';
import { FollowUpAccordionKeys } from '@/layouts/OrganizationLayout/NoteContent/followUp/FollowUpList/types';
import { notificationsStore } from '@/stores/global/RootStore';
import { ILocalStore } from '@/stores/interfaces/ILocalStore';
import { CollectionStore } from '@/stores/local/shared/CollectionStore';
import { MetaStore } from '@/stores/local/shared/MetaStore';
import call from '@/utils/api/api';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { ConfirmPopupStore } from '../ConfirmPopupStore';

import { FollowUpAccordionItemsStore } from './FollowUpAccordionItemsStore';
import { UpdateFollowUpStore } from './UpdateFollowUpStore';
import { getTimestamp } from './utils';

export type FollowUpStoreParams = {
  organizationId: string;
};

export class FollowUpStore implements ILocalStore {
  readonly meta = new MetaStore();
  readonly popupMeta = new MetaStore();
  readonly activeItems = new FollowUpAccordionItemsStore([FollowUpAccordionKeys.active]);
  readonly active = new CollectionStore<FollowUpEntity, FollowUpEntity>({
    parseItem: (data: FollowUpEntity) => data,
    getEntityKey: (data: FollowUpEntity) => String(data.id),
  });
  readonly closed = new CollectionStore<FollowUpEntity, FollowUpEntity>({
    parseItem: (data: FollowUpEntity) => data,
    getEntityKey: (data: FollowUpEntity) => String(data.id),
  });
  readonly removePopup: ConfirmPopupStore;
  readonly updateFollowUp: UpdateFollowUpStore;

  private _organizationId: string;
  private _lastRemovedId: string | null = null;

  constructor({ organizationId }: FollowUpStoreParams) {
    this._organizationId = organizationId;
    this.removePopup = new ConfirmPopupStore({
      onConfirm: (entityId) => this._onRemove(this._organizationId, entityId),
    });
    this.updateFollowUp = new UpdateFollowUpStore({ organizationId, onSuccess: () => this.fetchFollowUp(false) });

    makeObservable<FollowUpStore, '_lastRemovedId'>(this, {
      _lastRemovedId: observable,
      lastRemovedId: computed,
      fetchFollowUp: action.bound,
      clearLastRemoved: action.bound,
    });
  }

  get lastRemovedId(): string | null {
    return this._lastRemovedId;
  }

  clearLastRemoved() {
    this._lastRemovedId = null;
  }

  async remove(entityId: string) {
    return this._onRemove(this._organizationId, entityId);
  }

  private async _onRemove(organizationId: string, entityId: string) {
    this.popupMeta.setLoadedStartMeta();

    const response = await call({
      endpoint: ENDPOINTS.followUp.delete(organizationId, entityId),
      method: 'DELETE',
    });

    runInAction(() => {
      if (response.isError) {
        notificationsStore.showError(response.error.message ?? 'Не удалось удалить follow-up');
        this.popupMeta.setLoadedErrorMeta();

        return;
      }

      notificationsStore.showSuccess('Follow-up удален');
      this.popupMeta.setLoadedSuccessMeta();
      this._lastRemovedId = entityId;
      this.fetchFollowUp();
    });
  }

  async fetchFollowUp(withMeta = true) {
    if (withMeta) {
      this.active.clear();
      this.closed.clear();
      this.meta.setLoadedStartMeta();
    }

    const response = await call<{ items: FollowUpEntity[] }>({
      endpoint: ENDPOINTS.followUp.list(this._organizationId),
    });

    if (response.isError) {
      if (withMeta) {
        this.meta.setLoadedErrorMeta();
      }

      return;
    }

    runInAction(() => {
      const parsed = schemeParseMultiple(followUpSchema, response.data.items);

      const map = parsed.reduce<Record<FollowUpAccordionKeys, FollowUpEntity[]>>(
        (acc, item) => {
          if (item.isDone) {
            acc.closed.push(item);
          } else {
            acc.active.push(item);
          }

          return acc;
        },
        { active: [], closed: [] },
      );

      const sortByNewest = (a: FollowUpEntity, b: FollowUpEntity) => getTimestamp(b) - getTimestamp(a);

      map.active.sort(sortByNewest);
      map.closed.sort(sortByNewest);

      this.active.fromArray(map.active);
      this.closed.fromArray(map.closed);

      if (withMeta) {
        this.meta.setLoadedSuccessMeta();
      }
    });
  }

  destroy() {
    this.active.destroy();
    this.closed.destroy();
    this.activeItems.destroy();
    this.meta.destroy();
    this.popupMeta.destroy();
  }
}
